/**
 * Analytics service — typed wrapper over `lib/posthog`.
 *
 * Centralizes the event catalog and identifies + property contracts so
 * call sites don't import `posthog-js` directly. Adding a new event
 * means adding a method here, which keeps the catalog reviewable.
 */

import type { UserType } from "@shared/types";
import {
	captureException,
	identifyUser,
	resetUser,
	setUserProperties,
	track,
} from "@/lib/posthog";

// Aliased for ergonomic naming inside analytics code. Single source of
// truth for the underlying union lives in @shared/types.
export type AnalyticsUserType = UserType;

export interface AnalyticsUserTraits {
	userType?: AnalyticsUserType;
	homeownerProfileCompleted?: boolean;
	designerProfileCompleted?: boolean;
	contractorProfileCompleted?: boolean;
	createdAt?: string;
}

// ----------------------------------------------------------------------------
// Identification
// ----------------------------------------------------------------------------

function asRecord(traits: AnalyticsUserTraits): Record<string, unknown> {
	return { ...traits };
}

export const analytics = {
	identify(userId: string, traits?: AnalyticsUserTraits): void {
		identifyUser(userId, traits ? asRecord(traits) : undefined);
	},

	updateUserTraits(traits: AnalyticsUserTraits): void {
		setUserProperties(asRecord(traits));
	},

	reset(): void {
		resetUser();
	},

	captureException(error: unknown, context?: Record<string, unknown>): void {
		captureException(error, context);
	},

	// ------------------------------------------------------------------------
	// Auth
	// ------------------------------------------------------------------------
	auth: {
		otpSent(): void {
			track("Auth - OTP Sent");
		},
		otpVerified(props: { newUser: boolean }): void {
			track("Auth - OTP Verified", props);
		},
		loginFailed(props: { reason: string }): void {
			track("Auth - Login Failed", props);
		},
		logout(): void {
			track("Auth - Logout");
		},
	},

	// ------------------------------------------------------------------------
	// Onboarding
	// ------------------------------------------------------------------------
	onboarding: {
		userTypeSelected(props: { userType: AnalyticsUserType }): void {
			track("Onboarding - User Type Selected", props);
		},
	},

	// ------------------------------------------------------------------------
	// Profile
	// ------------------------------------------------------------------------
	profile: {
		homeownerCompleted(): void {
			track("Profile - Homeowner Completed");
			setUserProperties({ homeownerProfileCompleted: true });
		},
		contractorCompleted(): void {
			track("Profile - Contractor Completed");
			setUserProperties({ contractorProfileCompleted: true });
		},
		/** A saved profile edit. `fields` holds field names, never values. */
		updated(props: { userType: AnalyticsUserType; fields: string[] }): void {
			track("Profile - Updated", props);
		},
	},

	// ------------------------------------------------------------------------
	// Contractor (browsing/opportunity activity not already captured
	// server-side by serverAnalytics.contractor* in analytics-events.ts)
	// ------------------------------------------------------------------------
	contractor: {
		profileViewed(props: { contractorId: string }): void {
			track("Contractor - Profile Viewed", props);
		},
		opportunityViewed(props: { invitationId: string }): void {
			track("Contractor - Opportunity Viewed", props);
		},
	},
	// ------------------------------------------------------------------------
	// Share
	// ------------------------------------------------------------------------
	share: {
		/** A share delivered through a channel: web share, WhatsApp or copy. */
		sent(props: { artifactType: string; channel: string }): void {
			track("Share - Sent", props);
		},
	},
};

export type Analytics = typeof analytics;
